import { useCallback, useMemo, useRef, useState } from 'react';
import { IconRuler2 } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { resizePdf } from './resizePdfEngine';
import { zipPdfFiles } from '../split/splitPdfEngine';
import {
    applyPdfPageEdits,
    pagesHaveEdits,
    MAX_EDIT_PAGES_LARGE,
} from '../shared/pdfPageEditEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import PdfPageEditor from '../../components/pdf/PdfPageEditor';
import { usePdfPageEdits, fileKeyOf } from '../../components/pdf/usePdfPageEdits';
import Hint from '../../../../../components/common/Hint';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

// `ratio` is width / height from resizePdfEngine's page sizes, so each glyph is
// drawn at the paper's real proportion — the one visual difference between them.
const SIZE_OPTIONS = [
    { id: 'a4', labelKey: 'resizePdf.sizes.a4', ratio: 595.28 / 841.89 },
    { id: 'letter', labelKey: 'resizePdf.sizes.letter', ratio: 612 / 792 },
    { id: 'legal', labelKey: 'resizePdf.sizes.legal', ratio: 612 / 1008 },
    { id: 'custom', labelKey: 'resizePdf.sizes.custom' },
];

/** Tile in an icon tray: glyph over label, same shape as the image tools' trays. */
function iconTileClass(active) {
    return `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all ${
        active
            ? 'bg-white font-semibold text-pdf-cat-solid shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-orange-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/** A page outline at the paper's proportion (fits a 20px box); a ruler for custom. */
function PageSizeGlyph({ ratio }) {
    if (!ratio) return <IconRuler2 size={20} stroke={1.8} />;
    const height = 20;
    const width = Math.round(height * ratio);
    return <span aria-hidden="true" className="block rounded-[2px] border-[1.6px] border-current" style={{ width, height }} />;
}

export default function ResizePdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [preset, setPreset] = useState('a4');
    const [customWidthMm, setCustomWidthMm] = useState('210');
    const [customHeightMm, setCustomHeightMm] = useState('297');
    const [marginMm, setMarginMm] = useState(0);
    const [contentScale, setContentScale] = useState(100);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);

    const clearResults = useCallback(() => {
        setResults([]);
        setError(null);
    }, []);
    const { pagesByKey, updatePagesForKey, resetPageEdits } = usePdfPageEdits(files, clearResults, MAX_EDIT_PAGES_LARGE);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        if (!list.length) return;

        let hadError = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const f of list) {
                if (!(f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'))) {
                    hadError = true;
                    notifications.show({ title: t('common.error'), message: t('resizePdf.errors.invalidType'), color: 'red' });
                    continue;
                }
                if (f.size > MAX_FILE_BYTES) {
                    hadError = true;
                    notifications.show({ title: t('common.error'), message: t('resizePdf.errors.tooLarge'), color: 'red' });
                    continue;
                }
                next.push(f);
            }
            return next;
        });
        if (hadError) setError(t('resizePdf.errors.failed'));
        else setError(null);
        setResults([]);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in.
    usePdfPaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviewIndex((prev) => (index < prev ? prev - 1 : Math.max(0, Math.min(prev, files.length - 2))));
        setResults([]);
        setError(null);
    };

    const clearAll = () => {
        setFiles([]);
        setResults([]);
        setError(null);
        setPreviewIndex(0);
        resetPageEdits();
    };

    const handleDownloadZip = async () => {
        if (!results.length) return;
        const zip = await zipPdfFiles(results.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'resized-pdfs.zip');
    };

    const validateCustomSize = () => {
        const w = Number(customWidthMm);
        const h = Number(customHeightMm);
        if (!w || !h || w < 50 || h < 50 || w > 500 || h > 500) {
            const msg = t('resizePdf.errors.invalidSize');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!files.length || processing) return;
        if (preset === 'custom' && !validateCustomSize()) return;

        recordToolUsage('pdf/resize', { category: 'pdf' });

        setProcessing(true);
        setResults([]);
        setError(null);

        const options = {
            preset,
            customWidthMm: Number(customWidthMm),
            customHeightMm: Number(customHeightMm),
            marginMm,
            contentScale,
        };

        const done = [];
        // Names of the PDFs that failed, by reason: one bad PDF must not hide the others'
        // results, so these are only a warning when something did succeed.
        const failed = { password: [], other: [] };

        for (const file of files) {
            try {
                // Apply per-page edits (rotate / crop / delete / reorder / duplicate) before resizing.
                const pages = pagesByKey[fileKeyOf(file)];
                if (Array.isArray(pages) && pages.length && pages.every((p) => p.removed)) {
                    failed.other.push(file.name);
                    continue;
                }
                const source = Array.isArray(pages) && pagesHaveEdits(pages)
                    ? await applyPdfPageEdits(file, pages)
                    : file;
                const blob = await resizePdf(source, options);
                done.push({ name: `${file.name.replace(/\.pdf$/i, '')}-resized.pdf`, blob });
            } catch (err) {
                if (err?.message === 'password') failed.password.push(file.name);
                else failed.other.push(file.name);
            }
        }

        setResults(done);
        setProcessing(false);

        const problems = [
            [failed.password, 'resizePdf.errors.password'],
            [failed.other, 'resizePdf.errors.failed'],
        ].filter(([names]) => names.length);
        const color = done.length ? 'yellow' : 'red';
        const title = done.length ? t('common.warning') : t('common.error');
        for (const [names, key] of problems) {
            const msg = `${t(key)}${files.length > 1 ? ` (${names.join(', ')})` : ''}`;
            notifications.show({ title, message: msg, color });
        }
        if (done.length) {
            notifications.show({ title: t('common.success'), message: t('resizePdf.done'), color: 'green' });
        }
        // The panel shows the error only when there is nothing else to show.
        setError(!done.length && problems.length ? t(problems[0][1]) : null);
    };

    const canSubmit = files.length > 0 && !processing;

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun: canSubmit,
        processing,
        hasResult: results.length > 0,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);
    const statusText = getToolStatusText(status, t, {
        success: t('resizePdf.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const activeResult = results[previewIndex] ?? results[0] ?? null;
    const successLabel = results.length > 1
        ? `${t('resizePdf.resultTitle')} (${toBnDigits(results.length)})`
        : activeResult
            ? formatFileSize(activeResult.blob.size)
            : '';

    const resultDownloads = useMemo(() => {
        if (!results.length) return undefined;
        return results.map((r) => ({
            id: r.name,
            name: r.name,
            compressedSize: formatFileSize(r.blob.size),
            onDownload: () => downloadBlob(r.blob, r.name),
            onPrint: () => printBlob(r.blob),
        }));
    }, [results]);

    return (
        <ToolPageLayout
            slug="pdf/resize"
            subtitleKey="resizePdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(files.length) })}
            showAdd={files.length > 0}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={files.length > 0 || results.length > 0 || error ? clearAll : undefined}
            primaryAction={{
                label: t('resizePdf.apply'),
                onClick: handleSubmit,
                disabled: !canSubmit || results.length > 0,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {files.length === 0 ? (
                        <PdfDropZone
                            title={t('resizePdf.dropTitle')}
                            hint={t('resizePdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : null}

                    {files.length > 0 && (
                        <div className="max-h-[220px] space-y-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
                            {files.map((file, index) => (
                                <PdfUploadedFile
                                    key={`${file.name}-${index}`}
                                    name={file.name}
                                    meta={formatFileSize(file.size)}
                                    selected={previewIndex === index}
                                    onSelect={() => setPreviewIndex(index)}
                                    onRemove={() => removeFile(index)}
                                    removeLabel={t('resizePdf.removeFile')}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
            settings={(
                <>
                    {files.length > 0 && (
                        <>
                            <ToolCard title={t('resizePdf.sizeTitle')}>
                                <div className="space-y-3">
                                    {/* Shape over name — each outline is its paper's proportion
                                        (a ruler for custom), and the label stays visible
                                        because A4, Letter and Legal outlines look alike. */}
                                    <div className="grid grid-cols-4 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                        {SIZE_OPTIONS.map(({ id, labelKey, ratio }) => (
                                            <Hint key={id} label={t(`resizePdf.sizeTips.${id}`)}>
                                                <span className="flex min-w-0 [&>button]:w-full">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setPreset(id); setError(null); setResults([]); }}
                                                        aria-label={t(labelKey)}
                                                        aria-pressed={preset === id}
                                                        className={iconTileClass(preset === id)}
                                                    >
                                                        <PageSizeGlyph ratio={ratio} />
                                                        {t(labelKey)}
                                                    </button>
                                                </span>
                                            </Hint>
                                        ))}
                                    </div>
                                    {preset === 'custom' ? (
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1.5">
                                                <label htmlFor="resize-width" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                                    {t('resizePdf.widthLabel')}
                                                </label>
                                                <input
                                                    id="resize-width"
                                                    type="number"
                                                    min={50}
                                                    max={500}
                                                    value={customWidthMm}
                                                    onChange={(e) => { setCustomWidthMm(e.target.value); setError(null); setResults([]); }}
                                                    className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label htmlFor="resize-height" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                                    {t('resizePdf.heightLabel')}
                                                </label>
                                                <input
                                                    id="resize-height"
                                                    type="number"
                                                    min={50}
                                                    max={500}
                                                    value={customHeightMm}
                                                    onChange={(e) => { setCustomHeightMm(e.target.value); setError(null); setResults([]); }}
                                                    className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                                />
                                            </div>
                                        </div>
                                    ) : null}
                                    <p className="text-xs text-zinc-400 dark:text-zinc-500">{t('resizePdf.sizeHint')}</p>
                                </div>
                            </ToolCard>

                            <ToolCard title={t('resizePdf.marginLabel', { value: toBnDigits(marginMm) })}>
                                <div className="space-y-3">
                                    <input
                                        id="resize-margin"
                                        type="range"
                                        min={0}
                                        max={50}
                                        value={marginMm}
                                        onChange={(e) => { setMarginMm(Number(e.target.value)); setError(null); setResults([]); }}
                                        className="w-full accent-primary"
                                    />
                                    <p className="text-xs text-zinc-400 dark:text-zinc-500">{t('resizePdf.marginHint')}</p>
                                </div>
                            </ToolCard>

                            <ToolCard title={t('resizePdf.scaleLabel', { value: toBnDigits(contentScale) })}>
                                <div className="space-y-3">
                                    <input
                                        id="resize-scale"
                                        type="range"
                                        min={50}
                                        max={150}
                                        value={contentScale}
                                        onChange={(e) => { setContentScale(Number(e.target.value)); setError(null); setResults([]); }}
                                        className="w-full accent-primary"
                                    />
                                    <p className="text-xs text-zinc-400 dark:text-zinc-500">{t('resizePdf.scaleHint')}</p>
                                </div>
                            </ToolCard>
                        </>
                    )}

                </>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={t('resizePdf.processing')}
                    error={error}
                    successLabel={successLabel}
                    downloadLabel={t('resizePdf.download')}
                    downloads={resultDownloads}
                    downloadVariant="compress"
                    bundleDownload={results.length > 1
                        ? { label: t('resizePdf.downloadZip'), onClick: handleDownloadZip }
                        : undefined}
                    onDownload={results.length === 1 && activeResult
                        ? () => downloadBlob(activeResult.blob, activeResult.name)
                        : undefined}
                    onEditAgain={results.length > 0 ? clearResults : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={results.length > 0 ? clearAll : undefined}
                    againLabel={t('resizePdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('resizePdf.apply')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={files.length === 0 && !error}
                    pdfPreview={(files.length > 0 || activeResult) && !processing && !error}
                >
                    {!processing && !error && results.length > 0 ? (
                        <PdfDocPreview
                            files={files}
                            resultBlobs={results.map((r) => r.blob)}
                            activeIndex={previewIndex}
                            onActiveIndexChange={setPreviewIndex}
                        />
                    ) : !processing && !error && files.length > 0 ? (
                        <PdfPageEditor
                            files={files}
                            pagesByKey={pagesByKey}
                            onPagesChangeForKey={updatePagesForKey}
                            activeIndex={previewIndex}
                            onActiveIndexChange={setPreviewIndex}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
